<script setup>
   import { onMounted, ref, reactive, watch, watchEffect, inject } from 'vue'
   import { RouterLink, useRoute } from 'vue-router'
   import { limitCharacters } from '@/composables/strings'
   import { modalHide } from '@/composables/bootstrap'
   import { useElementVisibility } from '@vueuse/core'

   const route = useRoute()

   const $storeGinnels = inject('$storeGinnels')
   const $storeCollections = inject('$storeCollections')
   const $storeGinnel = inject('$storeGinnel')
   const $storeNavigation = inject('$storeNavigation')

   const currentPage = ref(1)
   watchEffect(() => currentPage.value = $storeGinnels.currentPage)
   const hasMoreData = ref(true)
   watchEffect(() => hasMoreData.value = $storeGinnels.hasMoreData)
   const gotGinnels = ref(false)
   watchEffect(() => gotGinnels.value = $storeGinnels.gotGinnels)

   /**
      * watch: targetIsVisible.value
      * @description Watches if the element comes in to the viewport.
      * Then sends a request to get more data.
   */
   const target = ref(null)
   const targetIsVisible = useElementVisibility(target)
   const loadingData = ref(false)
   watch(() => targetIsVisible.value, isVisible => {
      if(isVisible && hasMoreData.value) {
         loadingData.value = true

         setTimeout(() => {
            $storeGinnels.get({ collection_id: route.params?.collection_id, page: parseInt(currentPage.value) + 1 })
            loadingData.value = false
         }, 600)
      }
   })

   /*
   |--------------------------------------------------------------------------
   | Form
   |--------------------------------------------------------------------------
   */
   const form = reactive({
      collection_id: route.params?.collection_id,
      name: '',
      description: ''
   })
   const addGinnel = () => {
      $storeGinnels.add(form)
      form.name = ''
      form.description = ''
   }

   watch(() => route.params?.collection_id, (newData, oldData) => {
      form.collection_id = route.params?.collection_id
   })

   // Watch for changes and update the ginnels context
   const ginnels = ref([])
   watchEffect(() => {
      ginnels.value = $storeGinnels.ginnels
   })

   const pagination = ref(1)
   watchEffect(() => {
      pagination.value = $storeGinnels.ginnelsPagination
   })

   const collection = ref({})
   watchEffect(() => {
      let collections = $storeCollections.collections
      collection.value = collections.find(collection => collection.id == route.params?.collection_id)
   })

   // Watch for changes and close the ginnel modal
   watch(() => ginnels.value, newData => modalHide('ginnel-modal'), { deep: true })

   /*
   |--------------------------------------------------------------------------
   | Navigation Status
   |--------------------------------------------------------------------------
   */
   const openNav = navID => $storeNavigation.openNav(navID)
   const closeNav = navID => $storeNavigation.closeNav(navID)
</script>

<template>
   <div class="selector-heading d-flex justify-content-between w-100">
      <div class="nav-heading">
         <HelpTooltip tooltip="A Ginnel is an easy way for you to save and organize your files and snippets. Similar to how a traditional folder works." />
         Ginnels
      </div>
      <div class="ginnel-action-buttons">
         <div data-bs-toggle="modal" data-bs-target="#ginnel-modal" class="add-button">
            <FontAwesomeIcon icon="fas fa-plus" />
         </div>
      </div>
   </div>

   <div v-if="! ginnels?.length && gotGinnels" class="alert alert-dark mt-3">
      <span data-bs-toggle="modal" data-bs-target="#ginnel-modal" class="link d-flex align-items-center gap-2">
         <FontAwesomeIcon class="body-text-icon" icon="a-solid fa-folder-plus" /> Lets add a new Ginnel
      </span>
   </div>

   <template v-for="ginnel in ginnels" :key="ginnel.ginnel_id">
      <RouterLink
         :to="{ name: 'collection.ginnel', params: { collection_id: $route.params?.collection_id, ginnel_id: ginnel?.ginnel_id } }"
         @click="closeNav('ginnels')"
         class="ginnels-link">
         <div class="d-flex align-items-center gap-2 ginnel-title">
            <div class="ginnel-link-icon">
               <FontAwesomeIcon v-if="ginnel.ginnel_id == $route.params.ginnel_id" icon="fas fa-folder-open" />
               <FontAwesomeIcon v-else icon="fas fa-folder" />
            </div>
            {{ ginnel.ginnel_name }}
         </div>
         <span v-if="ginnel.description" class="ginnel-preview">{{ limitCharacters(ginnel.description, 120) }}</span>
      </RouterLink>
   </template>

   <div v-if="loadingData || ! gotGinnels" class="cg-row-content justify-content-center pt-2">
      <Loader size="40" />
   </div>

   <div v-if="ginnels?.length" ref="target" class="p-3"></div>

   <Modal id="ginnel-modal" size="medium">
      <template #header>Add a New Ginnel</template>
      <template #body>
         <div class="cg-column-content">
            <form @submit.prevent="addGinnel()" class="cg-column-content">
               <div>
                  <div class="label-group">
                     <label for="ginnel-name" class="form-label label-required">Ginnel Name</label>
                     <small class="label-description">Make it a descriptive name to help you in the future.</small>
                  </div>
                  <input id="ginnel-name" type="text" class="form-control" :class="{ 'is-valid': form.name }" v-model="form.name" placeholder="Ginnel Name" required>
               </div>

               <div>
                  <div class="label-group">
                     <label for="ginnel-desc" class="form-label">Ginnel Description</label>
                     <small class="label-description">An explanation of the use of this Ginnel</small>
                  </div>
                  <textarea v-autosize id="ginnel-desc" v-model="form.description" class="form-control"></textarea>
               </div>

               <div class="d-flex justify-content-end">
                  <button type="submit" class="btn btn-outline-primary">Add Ginnel</button>
               </div>
            </form>
         </div>
      </template>
   </Modal>
</template>